import type { Permission } from '@/auth/permissions'
import { mockLogin, USE_MOCK_AUTH } from '@/mocks/auth.mock'
import { authService } from '@/services/auth.service'
import type { LoginDto, User } from '@/types/user'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/**
 * Auth state — kept in MEMORY ONLY.
 *
 * The JWT lives in an HttpOnly cookie set by the backend; the frontend never
 * reads or stores it. On page refresh, this store is empty and `bootstrap()`
 * calls /auth/me — the cookie auths the call and we get the user back.
 *
 * NOTE: Do NOT add `persist: true` here. Persisting the user object to
 * localStorage would defeat the HttpOnly-cookie security model.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const isReady = ref(false)

  const isAuthenticated = computed(() => user.value !== null)

  function setUser(value: User | null) {
    user.value = value
  }

  function clear() {
    user.value = null
  }

  async function login(payload: LoginDto) {
    isLoading.value = true
    try {
      if (USE_MOCK_AUTH) {
        user.value = mockLogin(payload)
        return user.value
      }
      // The login call sets the HttpOnly JWT cookie on the browser.
      // We discard the minimal user payload from the login response and
      // immediately call /auth/me so we get the full profile (role + permissions).
      await authService.login(payload)
      await fetchMe()
      return user.value
    } finally {
      isLoading.value = false
    }
  }

  function applyMePayload(data: unknown) {
    const payload = data as {
      user: User
      roles: string[]
      permissions: string[]
      onboarding_status?: string
      onboarding_current_step?: number
    }
    user.value = {
      ...payload.user,
      role: (payload.roles?.[0] as User['role']) ?? undefined,
      permissions: (payload.permissions ?? []) as Permission[],
      ...(payload.onboarding_status !== undefined && {
        onboarding_status: payload.onboarding_status,
      }),
      ...(payload.onboarding_current_step !== undefined && {
        onboarding_current_step: payload.onboarding_current_step,
      }),
    }
    return user.value
  }

  async function fetchMe() {
    const { data } = await authService.me()
    return applyMePayload(data.data)
  }

  /**
   * Called once at app start. Tries to resolve the current user via the
   * HttpOnly cookie. If an `auth_token` query param is present (cross-domain
   * handoff from AI store registration on the storefront), we inject it as a
   * Bearer header for the initial /auth/me call, then call /auth/refresh to
   * HttpOnly cookie.
   */
  async function bootstrap() {
    if (isReady.value) return
    try {
      if (!USE_MOCK_AUTH) {
        await fetchMe()
      }
    } catch (err) {
      console.warn('[auth] bootstrap failed — not authenticated', err)
      /* not authed — leave user null */
    } finally {
      isReady.value = true
    }
  }

  async function logout() {
    try {
      if (!USE_MOCK_AUTH) await authService.logout()
    } catch {
      /* ignore */
    } finally {
      clear()
    }
  }

  return {
    user,
    isLoading,
    isReady,
    isAuthenticated,
    setUser,
    clear,
    login,
    logout,
    fetchMe,
    bootstrap,
  }
})
